import React, { useEffect, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import ChecklistSection from './ChecklistSection';
import useChecklistSection from './useChecklistSection';
import MissedStopCard from './MissedStopCard';
import useSupervisorAction from './useSupervisorAction';
import Button from '../../../components/atoms/forms/Button';
import CarryoverRerouteModal from './CarryoverRerouteModal';
import {
  MISSED_STOP_EXCLUSION_REASONS,
  isReasonComplete,
  reasonLabel,
  requiresFreeText,
} from './reasons';
import { groupStopsByRoute, getStopRowKey } from './utils';
import { formatWeekdayMonthDay } from '../../../utils/datetime';

const SECTION_ID = 'missed-stops';

function buildIncludedAuto(stop) {
  return {
    status: 'included',
    mode: 'auto',
    targetRouteName: null,
    stopId: stop.id || '',
    originalRoute: stop.route || '',
    originalDate: stop.stockingDate || '',
    toteCountPerStop:
      typeof stop.toteCountPerStop === 'number' ? stop.toteCountPerStop : null,
  };
}

const StyledDiv = styledComponent('div', {
  '& .msr-loading, & .msr-empty': {
    padding: theme.spacing.bodyPadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
  },
  '& .msr-banner': {
    background: theme.colors.accentSoftBg,
    borderLeft: `3px solid ${theme.colors.accent}`,
    borderRadius: theme.radii.input,
    padding: `${theme.spacing.labelGap} ${theme.spacing.bodyPadding}`,
    marginBottom: theme.spacing.fieldGap,
    fontSize: theme.typography.label.fontSize,
    color: theme.colors.textDark,
  },
});

function MissedStopsReviewSection({
  stockingDate,
  missedStops,
  todayRoutes,
  isLoading,
  stepNumber,
  fulfillmentCenter,
  market,
  reviewPhase,
  onCompletedChange,
  onAssignmentsChange,
  onGoToRoute,
  onRouteMissedStops,
  isEditorLocked,
  editorLockedTooltip,
  allowSpecificAssignment,
}) {
  const { fields, setField, markComplete, cancel, completed, loaded } =
    useChecklistSection({
      sectionId: SECTION_ID,
      stockingDate,
      initialFields: { decisions: {} },
      onCompletedChange,
    });

  const supervisorAction = useSupervisorAction({
    fulfillmentCenter,
    market,
    reviewDate: stockingDate,
    reviewPhase,
  });

  const groupedTodayRoutes = useMemo(
    () => groupStopsByRoute(todayRoutes),
    [todayRoutes],
  );

  const decisions = fields.decisions || {};

  // Seed every missed stop that has no saved decision to included-auto, so the
  // section opens with all stops checked. Decisions already in the draft (the
  // supervisor's saved choices) are preserved across refreshes.
  useEffect(() => {
    if (isLoading || !loaded) return;
    let changed = false;
    const next = { ...decisions };
    missedStops.forEach((stop) => {
      const rowKey = getStopRowKey(stop);
      if (!next[rowKey]) {
        next[rowKey] = buildIncludedAuto(stop);
        changed = true;
      }
    });
    if (changed) setField('decisions', next);
    // decisions is a fresh object each render; serialize for a stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(missedStops),
    JSON.stringify(decisions),
    isLoading,
    loaded,
  ]);

  const setDecision = useCallback(
    (rowKey, decision) => {
      setField('decisions', { ...decisions, [rowKey]: decision });
    },
    [decisions, setField],
  );

  const excludeStop = useCallback(
    (rowKey, stop, reasonCode, notes) => {
      setDecision(rowKey, {
        status: 'excluded',
        stopId: stop.id || '',
        originalRoute: stop.route || '',
        originalDate: stop.stockingDate || '',
        reasonCode,
        notes: notes || '',
      });
    },
    [setDecision],
  );

  const [showRerouteModal, setShowRerouteModal] = useState(false);

  // Totes come from the fetched stop record; excluded decisions don't carry
  // them, so join by rowKey.
  const toteByRowKey = useMemo(() => {
    const map = {};
    missedStops.forEach((stop) => {
      map[getStopRowKey(stop)] =
        typeof stop.toteCountPerStop === 'number'
          ? stop.toteCountPerStop
          : null;
    });
    return map;
  }, [missedStops]);

  // Modal inputs. `reasonLabel: null` marks an incomplete reason — the
  // modal renders it as "reason required" and blocks confirm.
  const rerouteModalData = useMemo(() => {
    const rerouting = [];
    const skipping = [];
    Object.entries(decisions).forEach(([rowKey, d]) => {
      if (!d) return;
      let totes = toteByRowKey[rowKey];
      if (typeof totes !== 'number') {
        totes =
          typeof d.toteCountPerStop === 'number' ? d.toteCountPerStop : null;
      }
      const base = { rowKey, name: d.stopId || rowKey, totes };
      if (d.status === 'included') {
        rerouting.push({
          ...base,
          // Defaults to auto like MissedStopCard does — note the tab's
          // normalizeAssignmentMap defaults missing mode to 'specific'.
          mode: d.mode || 'auto',
          targetRouteName: d.targetRouteName || null,
        });
        return;
      }
      let label = null;
      if (isReasonComplete({ value: d.reasonCode, note: d.notes })) {
        label = reasonLabel(d.reasonCode, MISSED_STOP_EXCLUSION_REASONS);
        if (requiresFreeText(d.reasonCode)) {
          label = `${label} — ${d.notes.trim()}`;
        }
      }
      skipping.push({ ...base, reasonLabel: label });
    });
    return { rerouting, skipping };
    // decisions is a fresh object each render; serialize for a stable dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(decisions), toteByRowKey]);

  const handleRerouteConfirm = useCallback(() => {
    setShowRerouteModal(false);
    onRouteMissedStops();
  }, [onRouteMissedStops]);

  let routeButtonTooltip;
  if (isEditorLocked) {
    routeButtonTooltip = editorLockedTooltip;
  } else if (missedStops.length === 0) {
    routeButtonTooltip = 'No carryover stops to route';
  }

  const includedAssignments = useMemo(() => {
    const out = {};
    Object.entries(decisions).forEach(([rowKey, d]) => {
      if (d && d.status === 'included') {
        out[rowKey] = {
          mode: d.mode,
          targetRouteName: d.targetRouteName,
          stopId: d.stopId,
          originalRoute: d.originalRoute,
          originalDate: d.originalDate,
          toteCountPerStop: d.toteCountPerStop,
        };
      }
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(decisions)]);

  // Bubble the included subset up to the parent so the route editor can
  // render ghost rows on affected routes (Day Of view) without coupling to
  // localStorage directly. Excluded stops never reach the parent.
  useEffect(() => {
    onAssignmentsChange(includedAssignments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(includedAssignments)]);

  // Mark Complete is gated until every decision is audit-ready: excluded
  // stops need a complete reason, and included specific-route stops need a
  // target route.
  const markCompleteDisabled = useMemo(
    () =>
      Object.values(decisions).some((d) => {
        if (!d) return true;
        if (d.status === 'excluded') {
          return !isReasonComplete({ value: d.reasonCode, note: d.notes });
        }
        return d.mode === 'specific' && !d.targetRouteName;
      }),
    [decisions],
  );

  // On Mark Complete, persist the local commit then emit one MissedStopReview
  // SupervisorAction per decision (append-only audit log).
  const handleMarkComplete = useCallback(() => {
    markComplete();
    Object.values(decisions).forEach((d) => {
      const data =
        d.status === 'excluded'
          ? {
              stopId: d.stopId,
              originalRoute: d.originalRoute,
              originalDate: d.originalDate,
              decision: 'exclude',
              reasonCode: d.reasonCode,
              notes: d.notes || '',
            }
          : {
              stopId: d.stopId,
              originalRoute: d.originalRoute,
              originalDate: d.originalDate,
              decision: 'reattempt',
              mode: d.mode,
              targetRouteName: d.targetRouteName || null,
            };
      supervisorAction.submit({ eventType: 'MissedStopReview', data });
    });
  }, [markComplete, supervisorAction, decisions]);

  // The carryover date is the most recent stocking day with unfinished
  // stops — derived from the data so it stays correct if the fetch widens
  // beyond the prior day. YYYY-MM-DD compares correctly as strings.
  const carryoverDateLabel = useMemo(() => {
    const latest = missedStops.reduce(
      (max, s) =>
        s.stockingDate && s.stockingDate > max ? s.stockingDate : max,
      '',
    );
    return formatWeekdayMonthDay(latest);
  }, [missedStops]);

  return (
    <StyledDiv data-testid="missed-stops-review">
      <ChecklistSection
        stepNumber={stepNumber}
        title={`Carryover · unfinished stops${
          carryoverDateLabel ? ` from ${carryoverDateLabel}` : ''
        }`}
        description="All known misses are set to reroute by default — deselect any that shouldn't go, with a reason."
        completed={completed}
        onCancel={cancel}
        onMarkComplete={handleMarkComplete}
        markCompleteDisabled={markCompleteDisabled}
        footerAction={
          <Button
            variant="secondary"
            onClick={() => setShowRerouteModal(true)}
            disabled={isEditorLocked || missedStops.length === 0}
            title={routeButtonTooltip}
            dataTestId="msr-route-missed-stops"
          >
            Route missed stops
          </Button>
        }
      >
        {!isLoading && missedStops.length > 0 && (
          <div className="msr-banner" data-testid="msr-banner">
            ✔ All stops below are <strong>selected to reroute</strong>. Uncheck
            any that shouldn’t be replanned — a reason is required when you
            deselect.
          </div>
        )}
        {isLoading && (
          <div className="msr-loading" data-testid="msr-loading">
            Loading missed stops…
          </div>
        )}
        {!isLoading && missedStops.length === 0 && (
          <div className="msr-empty" data-testid="msr-empty">
            No missed stops to review.
          </div>
        )}
        {!isLoading && missedStops.length > 0 && (
          <div data-testid="msr-list">
            {missedStops.map((stop) => {
              const rowKey = getStopRowKey(stop);
              return (
                <MissedStopCard
                  key={rowKey}
                  rowKey={rowKey}
                  missedStop={stop}
                  todayRoutes={groupedTodayRoutes}
                  decision={decisions[rowKey] || null}
                  onConfirm={(a) =>
                    setDecision(rowKey, { status: 'included', ...a })
                  }
                  onExclude={(reasonCode, notes) =>
                    excludeStop(rowKey, stop, reasonCode, notes)
                  }
                  onInclude={() => setDecision(rowKey, buildIncludedAuto(stop))}
                  onGoToRoute={onGoToRoute}
                  allowSpecificAssignment={allowSpecificAssignment}
                />
              );
            })}
          </div>
        )}
      </ChecklistSection>
      <CarryoverRerouteModal
        show={showRerouteModal}
        rerouting={rerouteModalData.rerouting}
        skipping={rerouteModalData.skipping}
        onConfirm={handleRerouteConfirm}
        onCancel={() => setShowRerouteModal(false)}
      />
    </StyledDiv>
  );
}

MissedStopsReviewSection.propTypes = {
  stockingDate: PropTypes.string.isRequired,
  missedStops: PropTypes.arrayOf(PropTypes.object),
  todayRoutes: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  stepNumber: PropTypes.number,
  fulfillmentCenter: PropTypes.string,
  market: PropTypes.string,
  reviewPhase: PropTypes.string,
  onCompletedChange: PropTypes.func,
  onAssignmentsChange: PropTypes.func,
  onGoToRoute: PropTypes.func,
  onRouteMissedStops: PropTypes.func,
  isEditorLocked: PropTypes.bool,
  editorLockedTooltip: PropTypes.string,
  allowSpecificAssignment: PropTypes.bool,
};

MissedStopsReviewSection.defaultProps = {
  missedStops: [],
  todayRoutes: [],
  isLoading: false,
  stepNumber: 3,
  fulfillmentCenter: '',
  market: '',
  reviewPhase: 'prep',
  onCompletedChange: () => {},
  onAssignmentsChange: () => {},
  onGoToRoute: () => {},
  onRouteMissedStops: () => {},
  isEditorLocked: false,
  editorLockedTooltip: '',
  allowSpecificAssignment: true,
};

export default MissedStopsReviewSection;
